import React from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Signup from './components/Auth/Signup';
import Login from './components/Auth/Login';
import Logout from './components/Auth/Logout';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import EmailVerification from './components/Auth/EmailVerification';
import EmailVerificationLanding from './components/Auth/EmailVerificationLanding';
import UploadVideo from './components/UploadVideo';
import AnnotateVideo from './components/AnnotateVideo';
import EditStudio from './components/QuickEdits/EditStudio';
import BrandKitPage from './components/QuickEdits/BrandKitPage';
import VideoLibrary from './components/VideoLibrary';
import EnhancedSubmitEditingRequest from './components/EnhancedSubmitEditingRequest';
import { useAuth } from './context/AuthContext';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import Profile from './components/Profile';
import MyProjects from './components/MyProjects';
import Insights from './components/Insights';
import EditProjects from './components/EditProjects';
import AllEditProjects from './components/AllEditProjects';
import EditProjectsForCreator from './components/EditProjectsForCreator';
import { ThemeProvider } from '@mui/material/styles';
import { Box, CssBaseline } from '@mui/material';
import theme, { brand } from './theme';
import MyEditors from './components/MyEditors';
import HowItWorks from './components/HowItWorks';
import ProjectDetails from './components/ProjectDetails';
import EditorInvitation from './components/EditorInvitation';
import EditorRegistration from './components/Auth/EditorRegistration';
import SessionExpiryDialog from './components/Auth/SessionExpiryDialog';
import MaintenanceNotice from './components/MaintenanceNotice';
import SecurityMonitoring from './components/Admin/SecurityMonitoring';
import BillingDashboard from './components/Billing/BillingDashboard';
import PaymentMethods from './components/Billing/PaymentMethods';
import PaymentSuccess from './components/Billing/PaymentSuccess';
import PaymentCancel from './components/Billing/PaymentCancel';
import MulticamNew from './components/Multicam/MulticamNew';
import AnnotateMulticam from './components/Multicam/AnnotateMulticam';
import NotFound from './components/NotFound';
import { CreatorRoute, DashboardRoute, PrivateRoute, PublicOnlyRoute, SuperAdminRoute } from './components/Auth/RouteGuards';

const App: React.FC = () => {
  const { user, sessionExpired } = useAuth();
  const location = useLocation();

  // List of public routes
  // Full-screen sign-in shell pages, with no app header ('/logout' shows a
  // brief sign-out screen in the same shell).
  const publicRoutes = ['/login', '/signup', '/forgot-password', '/reset-password', '/verify-email', '/verify-email-link', '/editor-invitation', '/editor-registration', '/logout'];

  // Check if the current route is a public route
  const isPublicRoute = publicRoutes.includes(location.pathname);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ 
        minHeight: '100vh',
        backgroundColor: brand.canvas,
      }}>
        {user && !isPublicRoute && <Header />}
        {/* Creator-only pages use CreatorRoute: an editor-only account is sent
            to its editor dashboard instead. Pages both roles use (profile,
            how-to, the editor dashboard and workspace) use PrivateRoute. */}
        <Routes>
          {/* A signed-in user opening these goes to the dashboard instead. */}
          <Route path="/signup" element={<PublicOnlyRoute><Signup /></PublicOnlyRoute>} />
          <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<EmailVerification />} />
          <Route path="/verify-email-link" element={<EmailVerificationLanding />} />
          <Route path="/editor-invitation" element={<EditorInvitation />} />
          <Route path="/editor-registration" element={<EditorRegistration />} />
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />
          <Route
            path="/my-projects"
            element={
              <CreatorRoute>
                <MyProjects />
              </CreatorRoute>
            }
          />
          <Route
            path="/insights"
            element={
              <CreatorRoute>
                <Insights />
              </CreatorRoute>
            }
          />
          <Route
            path="/project-details/:projectId"
            element={
              <CreatorRoute>
                <ProjectDetails />
              </CreatorRoute>
            }
          />
          <Route
            path="/edit-projects/"
            element={
              <PrivateRoute>
                <AllEditProjects />
              </PrivateRoute>
            }
          />
          <Route
            path="/edit-projects/:id"
            element={
              <PrivateRoute>
                <EditProjects />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <DashboardRoute>
                <Dashboard />
              </DashboardRoute>
            }
          />
          <Route
            path="/upload"
            element={
              <CreatorRoute>
                <UploadVideo />
              </CreatorRoute>
            }
          />
          <Route
            path="/annotate/:videoId"
            element={
              <CreatorRoute>
                <AnnotateVideo />
              </CreatorRoute>
            }
          />
          <Route
            path="/edit/:editId"
            element={
              <CreatorRoute>
                <EditStudio />
              </CreatorRoute>
            }
          />
          <Route
            path="/brand-kit"
            element={
              <CreatorRoute>
                <BrandKitPage />
              </CreatorRoute>
            }
          />
          <Route
            path="/library"
            element={
              <CreatorRoute>
                <VideoLibrary />
              </CreatorRoute>
            }
          />
          <Route
            path="/submit-editing-request/"
            element={
              <CreatorRoute>
                <EnhancedSubmitEditingRequest />
              </CreatorRoute>
            }
          />
          <Route
            path="/submit-editing-request/:id"
            element={
              <CreatorRoute>
                <EditProjectsForCreator />
              </CreatorRoute>
            }
          />
          <Route
            path="/my-editors"
            element={
              <CreatorRoute>
                <MyEditors />
              </CreatorRoute>
            }
          />
          <Route
            path="/how-to"
            element={
              <PrivateRoute>
                <HowItWorks />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/security-monitoring"
            element={
              <SuperAdminRoute>
                <SecurityMonitoring />
              </SuperAdminRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <CreatorRoute>
                <BillingDashboard />
              </CreatorRoute>
            }
          />
          <Route
            path="/billing/payment-methods"
            element={
              <CreatorRoute>
                <PaymentMethods />
              </CreatorRoute>
            }
          />
          <Route
            path="/billing/success"
            element={
              <CreatorRoute>
                <PaymentSuccess />
              </CreatorRoute>
            }
          />
          <Route
            path="/billing/cancel"
            element={
              <CreatorRoute>
                <PaymentCancel />
              </CreatorRoute>
            }
          />
          {/* Multicam Routes */}
          <Route
            path="/multicam/new"
            element={
              <CreatorRoute>
                <MulticamNew />
              </CreatorRoute>
            }
          />
          <Route
            path="/multicam/:multicamSetId/edit"
            element={
              <CreatorRoute>
                <MulticamNew />
              </CreatorRoute>
            }
          />
          <Route
            path="/annotate-multicam/:multicamSetId"
            element={
              <CreatorRoute>
                <AnnotateMulticam />
              </CreatorRoute>
            }
          />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          {/* Catch-all: an unknown URL used to render the header over a blank
              page with no way forward. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        
        {/* Session Expiry Dialog */}
        <SessionExpiryDialog 
          open={sessionExpired} 
          onClose={() => {}} // Handled internally by the dialog
        />

        {/* The API's maintenance window: a short "moving to a new database" notice. */}
        <MaintenanceNotice />
      </Box>
    </ThemeProvider>
  );
};

export default App;
